import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronRight, MessageCircle, LogOut } from "lucide-react";
import { capi, apiError, money } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { CustomerStatus } from "@/components/account/CustomerStatus";
import { RemoveRequestButton } from "@/components/account/RemoveRequestButton";

export default function MyRequests() {
  const { customer, logout } = useCustomer();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    capi.get("/account/requests").then((r) => setItems(r.data)).catch((e) => { setErr(apiError(e)); setItems([]); });
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8" data-testid="my-requests-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-accent text-kys-accent">My Account</p>
          <h1 className="mt-2 font-serif text-4xl tracking-tight text-kys-ink sm:text-5xl">My Custom Orders</h1>
          <p className="mt-2 text-sm text-kys-muted">Signed in as <span data-testid="account-email">{customer?.email}</span></p>
        </div>
        <button data-testid="account-logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-kys-blush-border px-4 py-2 text-xs font-semibold text-kys-charcoal transition hover:border-kys-ink">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>

      {err && <p className="mt-6 rounded bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}
      {items === null && <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-kys-accent" /></div>}
      {items?.length === 0 && !err && (
        <div data-testid="no-requests" className="mt-10 rounded-lg border border-dashed border-kys-blush-border bg-white p-10 text-center">
          <p className="font-serif text-2xl text-kys-ink">No custom requests yet</p>
          <p className="mt-2 text-sm text-kys-muted">Design your dream set and it'll show up here.</p>
          <Link to="/builder" className="mt-6 inline-flex rounded-full bg-kys-ink px-6 py-3 text-sm font-semibold text-white hover:bg-kys-accent">Build Your Set</Link>
        </div>
      )}

      <div className="mt-8 space-y-3">
        {items?.map((r, i) => (
          <Link key={r.request_number} to={`/account/requests/${r.request_number}`} data-testid={`my-request-${r.request_number}`}
            style={{ animationDelay: `${i * 60}ms` }}
            className="group flex animate-fade-in items-center justify-between gap-4 rounded-lg border border-kys-blush-border bg-white p-5 transition-colors hover:border-kys-accent">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm text-kys-ink">{r.request_number}</span>
                <CustomerStatus status={r.status} label={r.status_label} />
                {r.unread_by_customer && <span className="flex items-center gap-1 text-xs font-semibold text-kys-accent"><MessageCircle className="h-3.5 w-3.5" /> New message</span>}
              </div>
              <p className="mt-1 truncate text-sm text-kys-charcoal">{(r.items || []).map((it) => it.style_name).join(" · ")}</p>
              <p className="mt-0.5 text-xs text-kys-muted">Submitted {new Date(r.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <RemoveRequestButton number={r.request_number} status={r.status} onRemoved={(n) => setItems((xs) => xs.filter((x) => x.request_number !== n))} />
              {r.final_price ? <span className="font-mono text-sm text-kys-ink">{money(r.final_price)}</span> : null}
              <ChevronRight className="h-5 w-5 text-kys-muted transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
